import { Contact } from '../../api/contacts/types'
import { DepthAnalysis, DepthAnalysisQuestions, DepthAnalysisSections, FormSubmissionAnswers } from '../../api/inDepthAnalysis/types'
import { ConnectedQuestionsIdsToDelete } from './DynamicUI/DynamicUI.types'
import { Ids } from './types'

export const findConnectedQuestionsIdsToDelete = (data: DepthAnalysis | undefined) => {
    const ids: Ids[] = []

    data?.sections?.forEach((section) => {
        const obj: Ids = {
            questionsToDelete: [],
            section: 0,
        }
        section?.questions?.forEach((question) => {
            question?.options?.forEach((option) => {
                if (option.sectionVisibilityToggleId !== null) {
                    obj[`questionId_${question.id}`] = question.id
                    obj[`section`] = option.sectionVisibilityToggleId
                    obj['questionsToDelete'] = findQuestionIdsInSection(data, option.sectionVisibilityToggleId)
                    ids.push(obj)
                }
            })
        })
    })

    const areObjectsEqual = (obj1: Ids, obj2: Ids) => {
        return JSON.stringify(obj1) === JSON.stringify(obj2)
    }

    const filteredIds = ids.filter((item, index, self) => {
        return !self.some((otherItem, otherIndex) => {
            return otherIndex < index && areObjectsEqual(otherItem, item)
        })
    })

    const updatedData = filteredIds.map((item) => {
        let mergedQuestionsToDelete = [...item.questionsToDelete]
        let setionToHide = [item.section]

        filteredIds.forEach((otherItem) => {
            const questionId = Object.values(otherItem)[0]
            if (mergedQuestionsToDelete.includes(questionId)) {
                mergedQuestionsToDelete = [...new Set([...mergedQuestionsToDelete, ...otherItem.questionsToDelete])]
                setionToHide = [...new Set([...setionToHide, otherItem.section])]
            }
        })

        return { ...item, questionsToDelte: mergedQuestionsToDelete, section: setionToHide }
    })

    return updatedData
}

export const findQuestionIdsInSection = (data: { sections: DepthAnalysisSections[] }, sectionId: number) => {
    const section = data.sections?.find((sec) => sec.id === sectionId)
    return section ? section.questions?.map((questions: { id: number }) => questions.id) : []
}

export function findObjectByQuestionId(arr: ConnectedQuestionsIdsToDelete[], targetId: number) {
    return arr?.find((obj) => Object.entries(obj).some(([key, value]) => key.startsWith('questionId_') && value === targetId))
}

export const parseNumber = (formattedValue: string) => {
    return formattedValue.replace(/\./g, '').replace(',', '.')
}

export const convertEndpointDataInFormikData = (data: FormSubmissionAnswers[] | undefined) => {
    return data?.reduce(
        (acc, { question_id, type, riskFactor, selectedOption, stringValue, dateValue, floatValue, textValue }) => {
            const key = `question_${question_id}_${type}_${riskFactor}`
            acc[key] = selectedOption || stringValue || dateValue || floatValue || textValue
            return acc
        },
        {} as Record<string, string | number | undefined>
    )
}

export const updateRequiredInHiddenSectionsOnLoad = (sections: DepthAnalysisSections[] | undefined) => {
    const deepSections = sections && JSON.parse(JSON.stringify(sections))
    const arr = deepSections?.map((section: { hidden: boolean; questions: DepthAnalysisQuestions[] }) =>
        section.hidden
            ? {
                  ...section,
                  questions: section.questions.map((question) => ({
                      ...question,
                      required: false,
                  })),
              }
            : {
                  ...section,
                  questions: section.questions.map((question) => {
                      if (question.additionalQuestion === true) {
                          return {
                              ...question,
                              required: false,
                          }
                      } else {
                          return {
                              ...question,
                          }
                      }
                  }),
              }
    )
    return arr ?? null
}

export const updateSectionsVisibilityOnLoad = (sections: DepthAnalysisSections[] | undefined, answers: FormSubmissionAnswers[] | undefined) => {
    const updateSections = sections?.map((section: DepthAnalysisSections) => {
        if (section.questions?.some((question) => answers?.some((answer) => answer.question_id === question.id))) {
            return { ...section, hidden: false }
        }
        return section
    })
    return updateRequiredInHiddenSectionsOnLoad(updateSections)
}

export const mapContactDataToFormSubmissionAnswers = (contact: Contact | null | undefined): FormSubmissionAnswers[] => {
    const answers: FormSubmissionAnswers[] = []

    if (!contact) return answers

    const contactType = contact.contactType

    if (contactType === 'person') {
        const personFieldMappings = [
            {
                questionId: 3,
                type: 'string' as const,
                value: [contact.street, contact.streetNumber].filter(Boolean).join(' ') || null,
                fieldName: 'stringValue' as const,
            },
            {
                questionId: 4,
                type: 'string' as const,
                value: contact.city,
                fieldName: 'stringValue' as const,
            },
            {
                questionId: 5,
                type: 'string' as const,
                value: contact.contactCountry?.name,
                fieldName: 'stringValue' as const,
            },
            {
                questionId: 6,
                type: 'date' as const,
                value: contact.contactPerson?.birthDate,
                fieldName: 'dateValue' as const,
            },
            {
                questionId: 7,
                type: 'string' as const,
                value: contact.taxNumber,
                fieldName: 'stringValue' as const,
            },
        ]

        personFieldMappings.forEach(({ questionId, type, value, fieldName }) => {
            if (value) {
                answers.push({
                    question_id: questionId,
                    type,
                    riskFactor: 'NONE',
                    [fieldName]: value,
                })
            }
        })
    }

    // TODO: Add company case mapping here when needed

    return answers
}
